import { checkIsGSM } from "../../utils/smsUtils";

export function generateStepId() {
  return crypto.randomUUID();
}

export function calculateSMSSegments(message: string): number {
  if (!message) return 0;
  const isGSM7 = checkIsGSM(message);
  const charsPerSegment = isGSM7 ? 160 : 70;
  const charsPerSegmentConcatenated = isGSM7 ? 153 : 67;
  if (message.length <= charsPerSegment) return 1;
  const remainingChars = message.length - charsPerSegment;
  return 1 + Math.ceil(remainingChars / charsPerSegmentConcatenated);
}

export function getMaxCharsForSegments({ message, segments }: { message: string; segments: number }): number {
  if (segments === 0) return 160;
  const isGSM7 = checkIsGSM(message);
  const charsPerSegment = isGSM7 ? 160 : 70;
  const charsPerSegmentConcatenated = isGSM7 ? 153 : 67;
  if (segments === 1) return charsPerSegment;
  return charsPerSegment + (segments - 1) * charsPerSegmentConcatenated;
}

export function getValidTimeZone({ value }: { value: string }) {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value });
    return value;
  } catch {
    return "UTC";
  }
}

export function formatDateInTimeZoneForInput({
  value,
  timeZone,
}: {
  value?: string;
  timeZone: string;
}): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: getValidTimeZone({ value: timeZone }),
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(date);

  const partMap = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  const year = partMap.year ?? "0000";
  const month = partMap.month ?? "01";
  const day = partMap.day ?? "01";
  const hour = partMap.hour ?? "00";
  const minute = partMap.minute ?? "00";
  return `${year}-${month}-${day}T${hour}:${minute}`;
}

export function getTimeZoneOffsetInMs({
  date,
  timeZone,
}: {
  date: Date;
  timeZone: string;
}) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: getValidTimeZone({ value: timeZone }),
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(date);

  const partMap = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  const year = Number(partMap.year);
  const month = Number(partMap.month);
  const day = Number(partMap.day);
  const hour = Number(partMap.hour);
  const minute = Number(partMap.minute);
  const second = Number(partMap.second);
  const asUtc = Date.UTC(year, month - 1, day, hour, minute, second);
  return asUtc - date.getTime();
}

export function zonedInputToUtcIso({
  value,
  timeZone,
}: {
  value?: string;
  timeZone: string;
}) {
  if (!value) return "";
  const [datePart, timePart] = value.split("T");
  if (!datePart || !timePart) return "";

  const [yearRaw, monthRaw, dayRaw] = datePart.split("-");
  const [hourRaw, minuteRaw] = timePart.split(":");
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  const day = Number(dayRaw);
  const hour = Number(hourRaw);
  const minute = Number(minuteRaw);
  if ([year, month, day, hour, minute].some((n) => !Number.isFinite(n))) {
    return "";
  }

  const firstGuessUtcMs = Date.UTC(year, month - 1, day, hour, minute, 0, 0);
  const firstOffset = getTimeZoneOffsetInMs({
    date: new Date(firstGuessUtcMs),
    timeZone,
  });
  const adjustedUtcMs = firstGuessUtcMs - firstOffset;
  const secondOffset = getTimeZoneOffsetInMs({
    date: new Date(adjustedUtcMs),
    timeZone,
  });
  const normalizedUtcMs = firstGuessUtcMs - secondOffset;

  const utcDate = new Date(normalizedUtcMs);
  if (Number.isNaN(utcDate.getTime())) {
    return "";
  }
  return utcDate.toISOString();
}
